import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { FaSave, FaTimes, FaSpinner, FaTrash } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";

const VendorsForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
	email: "",
	vendor_code: "",
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL ;
  const {logout} = useAuth();

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
    };
  };

  useEffect(() => {
    if (isEdit) {
      axios
        .get(`${API}/vendors/${id}`, getAuthHeader())
        .then((res) => {
          const vendor = res.data;
		  if (!vendor || Object.keys(vendor).length === 0) {
			  alert("Vendor not found or not accessible.");
			  navigate("/vendors");
			  return;
			}
			
          setFormData({
            name: vendor.name || "",
            phone: vendor.phone || "",
            address: vendor.address || "",
			email: vendor.email || "",
			vendor_code: vendor.vendor_code || "",
          });
        })
        .catch((err:any) => {
          //console.error("Failed to fetch vendor", err);
		  if (err.response?.status === 401) {
		  logout();
		  navigate("/login"); // redirect ke login
		}
        });
    }
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      alert("Fill all mandatory fields!");
      return;
    }

    const confirmSave = window.confirm("Do you want to save this data?");
    if (!confirmSave) return;

    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const vendor_code = "1";
      const payload = isEdit
        ? { ...formData }
        : {
            ...formData,
            tenant_id: user.tenantId,
			created_by_user_id: user.id,
          };

      if (isEdit) {
        await axios.put(`${API}/vendors/${id}`, payload, getAuthHeader());
        alert("Vendor updated!");
      } else {
        await axios.post(`${API}/vendors`, payload, getAuthHeader());
        alert("Vendor saved!");
      }

      navigate("/vendors");
    } catch (error) {
      //console.error("Failed to save vendor:", error);
      alert("Failed to save vendor");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete this data?");
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API}/vendors/${id}`, getAuthHeader());
      alert("Vendor deleted");
      navigate("/vendors");
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete vendor");
    }
  };

  return (
    <div className="p-1 max-w-6xl mx-auto">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-700">
            {isEdit ? "Edit Vendor" : "Add Vendor"}
          </h2>
          <div className="flex gap-2">
            <button
              type="submit"
              form="vendor-form"
              disabled={loading}
              className="bg-blue-600 text-white px-2 py-1 rounded flex items-center gap-2 text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaSave />}
              Save
            </button>
            <button
              onClick={() => navigate("/vendors")}
              disabled={loading}
              className="bg-gray-300 text-gray-800 px-2 py-1 rounded flex items-center gap-2 text-sm hover:bg-gray-400 disabled:opacity-50"
            >
              <FaTimes />
              Cancel
            </button>
            {isEdit && (
              <button
                onClick={handleDelete}
                disabled={loading}
                className="bg-red-600 text-white px-2 py-1 rounded flex items-center gap-2 text-sm hover:bg-red-700 disabled:opacity-50"
              >
                <FaTrash />
                Delete
              </button>
            )}
          </div>
        </div>

        <form id="vendor-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            
			{/* Name */}
            <div className="flex items-center">
              <label className="w-32 text-sm font-medium">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="flex-1 border rounded px-2 py-1 text-sm"
              />
            </div>

            {/* Phone */}
            <div className="flex items-center">
              <label className="w-32 text-sm font-medium">
                Phone 
              </label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="flex-1 border rounded px-2 py-1 text-sm"
              />
            </div>

            {/* Email */}
            <div className="flex items-start">
              <label className="w-32 text-sm font-medium mt-2">
                Email
              </label>
              <input
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="flex-1 border rounded px-2 py-1 text-sm"
              />
            </div>

            {/* Address */}
            <div className="flex items-start">
              <label className="w-32 text-sm font-medium mt-2">
                Address
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows={3}
                className="flex-1 border rounded px-2 py-1 text-sm"
              />
            </div>
			
          </div>
        </form>
      </div>
    </div>
  );
};

export default VendorsForm;
